import React from "react";
import { ScanLine, ShieldAlert, Upload, X, Wallet, Landmark, Check } from "lucide-react";
import { Modal, MODAL_HEADER, MODAL_ACTIONS } from "../shared/Modal";
import { useAppContext } from "../../AppContext";

/* -----------------------------------------------------------------------------
 * ConfirmOrderModal — "Completar Pago Parcial". Shown when the user's earlier
 * payment covered only part of the total and they need to submit an
 * additional proof to complete the order. Extracted verbatim from
 * AppModals.jsx.
 * --------------------------------------------------------------------------- */
export function ConfirmOrderModal() {
  const ctx = useAppContext();
  const {
    completePartialOrder, setCompletePartialOrder,
    partialAmount, setPartialAmount,
    partialFile, setPartialFile, partialFileName, setPartialFileName,
    completingPartial, partialErrors, handleSubmitPartialPayment,
    remainingForOrder,
    partialPmId, setPartialPmId,
    partialUseCredit, setPartialUseCredit,
    paymentMethods, user,
  } = ctx;

  if (!completePartialOrder) return null;

  const remaining = remainingForOrder(completePartialOrder);
  const availableMethods = (paymentMethods || []).filter((m) => m.active !== false);
  const creditBalance = Number(user?.credit_balance_bs || 0);
  const creditToUse = partialUseCredit ? Math.min(creditBalance, remaining) : 0;
  const creditCoversAll = creditToUse >= remaining - 0.5;
  const stillToPay = Math.max(0, remaining - creditToUse);
  const selectedPm = availableMethods?.find((m) => m.id === partialPmId);
  const selectedCurrency = selectedPm?.type === "USD_PLATFORM" ? "USD" : "BS";
  const rate = Number(ctx.exchangeRate || 0);
  const remainingUsd = rate > 0 ? remaining / rate : 0;
  const stillToPayUsd = rate > 0 ? stillToPay / rate : 0;

  return (
        <Modal onClose={() => !completingPartial && setCompletePartialOrder(null)} size="lg" testId="complete-partial-modal">
          <div className={MODAL_HEADER}>
            <div>
              <span className="text-xs font-mono text-orange-400">COMPLETAR PAGO PARCIAL</span>
              <h3 className="text-lg font-extrabold text-white">{completePartialOrder.gameName} - {completePartialOrder.packageName}</h3>
              <p className="text-xs text-zinc-500 font-mono">{completePartialOrder.id}</p>
            </div>
            <button data-testid="confirm-order-modal-button-1" onClick={() => !completingPartial && setCompletePartialOrder(null)} className="text-zinc-400 hover:text-white bg-zinc-800 p-2 rounded-full disabled:opacity-40" disabled={completingPartial}>
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 rounded-2xl bg-orange-950/30 border border-orange-500/30 space-y-2 text-xs font-mono">
            <div className="flex justify-between"><span className="text-zinc-400">Precio del paquete:</span><span className="text-white">Bs {Math.round(completePartialOrder.priceBs)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Ya pagado:</span><span className="text-cyan-400">Bs {Math.round((completePartialOrder.paidAmountBs + (completePartialOrder.additionalPayments || []).reduce((a, p) => a + p.paidAmountBs, 0)))}</span></div>
            <div className="flex justify-between border-t border-orange-500/20 pt-2">
              <span className="text-orange-300 font-bold">Falta pagar:</span>
              <span className="text-orange-300 font-bold text-base" data-testid="partial-remaining">Bs {Math.round(remaining)}</span>
            </div>
            <div className="flex justify-between" data-testid="partial-remaining-usd">
              <span className="text-zinc-500">Faltante USD/USDT:</span>
              <span className="text-orange-200">$ {remainingUsd.toFixed(2)}</span>
            </div>
            {creditToUse > 0 && (
              <>
                <div className="flex justify-between"><span className="text-zinc-400">Saldo a favor aplicado:</span><span className="text-green-400" data-testid="partial-credit-applied">- Bs {Math.round(creditToUse)}</span></div>
                <div className="flex justify-between border-t border-orange-500/20 pt-2">
                  <span className="text-white font-bold">Total a transferir:</span>
                  <span className="text-white font-bold text-base" data-testid="partial-still-to-pay">Bs {Math.round(stillToPay)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Total USD/USDT:</span>
                  <span className="text-white font-bold">$ {stillToPayUsd.toFixed(2)}</span>
                </div>
              </>
            )}
          </div>

          {creditBalance > 0.5 && (
            <button
              type="button"
              onClick={() => setPartialUseCredit(!partialUseCredit)}
              data-testid="partial-use-credit-toggle"
              aria-pressed={partialUseCredit}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${
                partialUseCredit
                  ? "bg-green-950/40 border-green-500/60"
                  : "bg-zinc-950 border-zinc-700 hover:border-green-500/40"
              }`}
            >
              <span className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${
                partialUseCredit ? "bg-green-500/30 border-green-400" : "border-zinc-600"
              }`}>
                {partialUseCredit && <Check className="w-3.5 h-3.5 text-green-300" />}
              </span>
              <Wallet className="w-4 h-4 text-green-400 flex-shrink-0" />
              <span className="text-xs">
                <strong className="text-white">Usar mi saldo a favor</strong>
                <span className="block text-[11px] text-zinc-400 font-mono">
                  Disponible: Bs {Math.round(creditBalance)}
                  {partialUseCredit ? ` · se aplicarán Bs ${Math.round(creditToUse)}` : ""}
                </span>
              </span>
            </button>
          )}

          {creditCoversAll && (
            <div className="p-3 rounded-xl bg-green-950/30 border border-green-500/40 text-green-200 text-[11px]" data-testid="partial-credit-covers-all">
              Tu saldo a favor cubre todo el faltante: no necesitas subir comprobante.
              Pulsa «Completar con mi saldo» y tu pedido pasará a verificación.
            </div>
          )}

          {partialErrors.length > 0 && (
            <div className="p-4 rounded-xl bg-red-950/40 border border-red-500/50 space-y-2">
              <div className="flex items-center gap-2 text-red-300 font-bold text-xs">
                <ShieldAlert className="w-4 h-4" /> Comprobante Rechazado
              </div>
              <ul className="text-xs text-red-200 list-disc pl-5 space-y-1">
                {partialErrors?.map((err, i) => <li key={`perr-${i}-${err}`}>{err}</li>)}
              </ul>
            </div>
          )}

          <form data-testid="confirm-order-modal-form-2" onSubmit={handleSubmitPartialPayment} className="space-y-4">
            {!creditCoversAll && (
              <div data-testid="partial-payment-methods">
                <label className="block text-xs font-mono text-zinc-400 mb-1">¿A dónde hiciste este pago?</label>
                <div className="grid grid-cols-2 gap-2">
                  {availableMethods?.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setPartialPmId(m.id)}
                      data-testid={`partial-pm-${m.id}`}
                      aria-pressed={partialPmId === m.id}
                      className={`p-2.5 rounded-xl border text-left transition-colors ${
                        partialPmId === m.id
                          ? "bg-orange-950/40 border-orange-500/70"
                          : "bg-zinc-950 border-zinc-700 hover:border-orange-500/40"
                      }`}
                    >
                      <span className="flex items-center gap-1.5 text-[11px] font-bold text-white">
                        <Landmark className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                        {m.bankName || m.platformName || "Banco"}
                      </span>
                      <span className="block text-[10px] text-zinc-500 font-mono mt-0.5">
                        {m.type === "USD_PLATFORM" ? "USD/USDT" : (m.type === "PAGO_MOVIL" ? "Pago Móvil" : "Transferencia")}
                      </span>
                    </button>
                  ))}
                </div>
                {selectedPm && (
                  <div className="mt-2 p-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-[11px] font-mono text-zinc-300 space-y-0.5" data-testid="partial-pm-details">
                    {selectedPm.phone && <p>Teléfono: <strong className="text-white">{selectedPm.phone}</strong></p>}
                    {selectedPm.documentId && <p>Cédula/RIF: <strong className="text-white">{selectedPm.documentId}</strong></p>}
                    {selectedPm.accountNumber && <p>Cuenta: <strong className="text-white">{selectedPm.accountNumber}</strong></p>}
                    {selectedPm.holderName && <p>Titular: <strong className="text-white">{selectedPm.holderName}</strong></p>}
                  </div>
                )}
              </div>
            )}

            {!creditCoversAll && (
            <div>
              <label className="block text-xs font-mono text-zinc-400 mb-1" data-testid="partial-amount-label">
                Monto de este pago ({selectedCurrency === "USD" ? "USD/USDT" : "Bs"}):
              </label>
              <input type="number" step="0.01" required data-testid="partial-amount-input" value={partialAmount} onChange={(e) => setPartialAmount(e.target.value)}
                placeholder={selectedCurrency === "USD" ? stillToPayUsd.toFixed(2) : String(Math.round(stillToPay))} className="w-full bg-zinc-950 border border-zinc-700 rounded-xl px-4 py-2.5 text-white text-sm font-mono" />
            </div>
            )}

            {!creditCoversAll && (
            <div>
              <label className="block text-xs font-mono text-zinc-400 mb-1">Comprobante del pago adicional:</label>
              <label htmlFor="partial-proof-input" className="border-2 border-dashed border-zinc-700 rounded-xl p-4 text-center cursor-pointer hover:border-orange-500 bg-zinc-950 block">
                <Upload className="w-6 h-6 text-orange-400 mx-auto mb-1" />
                <p className="text-xs text-zinc-300">{partialFileName ? <strong className="text-green-400">{partialFileName}</strong> : "Sube tu nueva captura de pago"}</p>
                <p className="text-[10px] text-zinc-500 mt-1">JPG, PNG o WEBP - máx 10MB</p>
                <input id="partial-proof-input" type="file" accept="image/jpeg,image/png,image/webp" data-testid="partial-proof-file-input"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setPartialFile(e.target.files[0]);
                      setPartialFileName(e.target.files[0].name);
                    }
                  }} className="hidden" />
              </label>
            </div>
            )}

            {!creditCoversAll && (
            <div className="p-3 rounded-xl bg-cyan-950/30 border border-cyan-500/30 text-cyan-200 text-[11px]">
              La IA extraerá y verificará automáticamente la referencia, banco destino y fecha del comprobante. Debe ser <strong>única</strong> y del <strong>día de hoy</strong>.
            </div>
            )}

            <div className={MODAL_ACTIONS}>
              <button data-testid="confirm-order-modal-button-3" type="button" onClick={() => setCompletePartialOrder(null)} disabled={completingPartial}
                className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-3 rounded-xl text-xs disabled:opacity-40">Cancelar</button>
              <button type="submit" disabled={completingPartial} data-testid="submit-partial-payment-btn"
                className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:opacity-90 text-white font-bold py-3 rounded-xl text-xs shadow-lg disabled:opacity-60 flex items-center justify-center gap-2">
                {completingPartial
                  ? <><ScanLine className="w-4 h-4 animate-pulse" /> Verificando...</>
                  : creditCoversAll ? "Completar con mi saldo" : "Enviar Pago Adicional"}
              </button>
            </div>
          </form>
        </Modal>
  );
}
